import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import { useFetchWgHeaderData } from 'pages/WellGuardian/hooks/useFetchWgHeaderData';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { useCountryState, useSelectedAsset, useSelectedSite } from '../../../../redux/WellGuardian/selectors';
import wellGuardianApiClient from 'services/networking/wellGuardianApiClient';
import { WellInfoForSite } from 'types/wellGuardian';

import styles from './WellHealthHeader.module.scss';
import SelectWell from '../SelectWell';
import SiteSelector from '../SiteSelector';

const WellHealthHeader: React.FunctionComponent = () => {
  const intl = useIntl();
  const asset = useSelectedAsset();
  const site = useSelectedSite();
  const countryState = useCountryState();

  const { tuwi } = useParams<{ tuwi?: string }>();
  const { wellInfoLoading, wellInfo } = useFetchWgHeaderData(tuwi, intl);
  const [wellsForSiteLoading, setWellsForSiteLoading] = useState<boolean>(false);
  const [wellsForSite, setWellsForSite] = useState<WellInfoForSite[]>([]);

  const fetchWellsForSite = useCallback(async () => {
    if (!countryState) return;

    setWellsForSiteLoading(true);

    try {
      const wellsResponse = await wellGuardianApiClient.getWellsInfoForSite(
        asset,
        site,
        countryState,
      );
      const wellsForSelectedSite = wellsResponse.map(item => ({
        wellName: item.wellName,
        wellViewId: item.wellViewId,
      }));

      setWellsForSite(wellsForSelectedSite);
    } catch {
      setWellsForSite([]);
    } finally {
      setWellsForSiteLoading(false);
    }
  }, [asset, site, countryState]);

  useEffect(() => {
    fetchWellsForSite();
  }, [fetchWellsForSite]);

  if (wellInfoLoading) {
    return (
      <Grid direction="row" className={styles.wellInfoLoading}>
        <LinearProgress classes={{ barColorPrimary: styles.barColor }} />
      </Grid>
    );
  }

  return (
    <>
      <Grid direction="row" className={styles.wellName}>
        <div className={styles.siteSelectorContainer}>
          <SiteSelector variant="dark" color={true} />
        </div>
      </Grid>
      <Grid direction="row" className={styles.wellNames}>
        <span className={styles.title}>Well Name: </span>
        <SelectWell
          wellsForSite={wellsForSite}
          wellsForSiteLoading={wellsForSiteLoading}
          wellInfo={wellInfo}
        />
      </Grid>
      <Grid
        container
        direction="row"
        className={styles.wellInfoContainer}
        data-testid="wg-header-well-type-grid"
      />
    </>
  );
};

export default WellHealthHeader;
